import { useCallback, useEffect, useState } from 'react';

/** localStorage key for pre-login accessibility mode (login / public pages). */
export const A11Y_LOGIN_MODE_STORAGE_KEY = 'a11y-login-mode-enabled';

/** Applied on `html` when login accessibility mode is active. */
export const A11Y_LOGIN_MODE_CLASS = 'a11y-login-mode';

const A11Y_CLASS_NAMES = [
  'a11y-text-large',
  'a11y-text-larger',
  'a11y-text-spacing-relaxed',
  'a11y-high-contrast',
  'a11y-underline-links',
  'a11y-reduce-motion',
  'a11y-focus-strong',
] as const;

export const isLoginA11yModeEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(A11Y_LOGIN_MODE_STORAGE_KEY) === 'true';
};

const setLoginA11yModeEnabled = (enabled: boolean): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    A11Y_LOGIN_MODE_STORAGE_KEY,
    enabled ? 'true' : 'false'
  );
};

const clearA11yClasses = (): void => {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  A11Y_CLASS_NAMES.forEach((className) => html.classList.remove(className));
  html.classList.remove(A11Y_LOGIN_MODE_CLASS);
  html.style.removeProperty('--a11y-font-scale');
};

/**
 * Toggle login-page accessibility mode.
 * When enabled, forces light theme + high-contrast a11y classes.
 * When disabled, restores theme via optional callback (apps wire theme-preference).
 */
export const applyLoginA11yMode = (
  enabled: boolean,
  onDisabled?: () => void
): void => {
  if (typeof document === 'undefined') return;
  setLoginA11yModeEnabled(enabled);
  const html = document.documentElement;

  if (enabled) {
    html.classList.add(A11Y_LOGIN_MODE_CLASS);
    html.classList.add('a11y-high-contrast');
    html.classList.add('a11y-underline-links');
    html.classList.add('a11y-focus-strong');
    html.classList.add('a11y-text-spacing-relaxed');
    html.style.setProperty('--a11y-font-scale', '1');
    html.dataset.theme = 'light';
    return;
  }

  clearA11yClasses();
  onDisabled?.();
};

/** Shared accessibility mode state for login / signup public auth pages. */
export function useAuthA11yMode(onDisabled?: () => void) {
  const [a11yMode, setA11yMode] = useState(() => isLoginA11yModeEnabled());

  useEffect(() => {
    applyLoginA11yMode(a11yMode, onDisabled);
  }, [a11yMode, onDisabled]);

  const handleA11yModeChange = useCallback(
    (checked: boolean) => {
      setA11yMode(checked);
      applyLoginA11yMode(checked, onDisabled);
    },
    [onDisabled]
  );

  const authFormClassName = a11yMode ? 'auth-form auth-form--a11y' : 'auth-form';

  return { a11yMode, handleA11yModeChange, authFormClassName };
}
